/**
 * Session Timer — with lockout-aware reload
 *
 * - Hidden on /verify-otp (lockout has its own server-driven timer)
 * - Reload handler preserves email identifier and re-fetches server state
 * - Does NOT use window.location.reload() — uses controlled re-fetch
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { secureSessionStorage, secureLocalStorage } from '../utils/secure-session';

const SESSION_DURATION = 30 * 60; // 30 minutes
const AUTOSAVE_INTERVAL = 30000;
const ANNOUNCE_AT = [600, 300, 60];

const SessionTimer = () => {
  const location = useLocation();
  const [secondsLeft, setSecondsLeft] = useState(SESSION_DURATION);
  const [expired, setExpired] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const announcedRef = useRef(new Set());
  const intervalRef = useRef(null);

  // DISABLE on OTP and lockout pages — those have their own server-driven timers
  const isOtpPage = location.pathname === '/verify-otp';
  const isLockoutPage = location.pathname === '/verify-otp'; // same page handles lockout

  const autoSave = useCallback(async () => {
    try {
      const formState = await secureSessionStorage.getItem('followup_form_state');
      if (formState) {
        const backupData = {
          data: formState,
          timestamp: Date.now(),
          expiresAt: Date.now() + (30 * 60 * 1000)
        };
        secureLocalStorage.setItem('followup_form_backup', backupData).catch(() => {});
        secureLocalStorage.setItem('followup_form_backup_time', new Date().toISOString()).catch(() => {});
      }
    } catch (e) { /* fail silently */ }
  }, []);

  useEffect(() => {
    if (isOtpPage) return; // Don't run timer on OTP page
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); setExpired(true); autoSave(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [autoSave, isOtpPage]);

  useEffect(() => {
    if (isOtpPage) return;
    const saveInterval = setInterval(autoSave, AUTOSAVE_INTERVAL);
    return () => clearInterval(saveInterval);
  }, [autoSave, isOtpPage]);

  useEffect(() => {
    if (ANNOUNCE_AT.includes(secondsLeft) && !announcedRef.current.has(secondsLeft)) {
      announcedRef.current.add(secondsLeft);
      const mins = Math.ceil(secondsLeft / 60);
      setAnnouncement(`Session expires in ${mins} minute${mins !== 1 ? 's' : ''}`);
      setTimeout(() => setAnnouncement(''), 5000);
    }
  }, [secondsLeft]);

  // Don't render anything on OTP page
  if (isOtpPage) return null;

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isVisible = secondsLeft <= 600;
  const isWarning = secondsLeft <= 300;
  const isCritical = secondsLeft <= 60;

  // Session expired dialog — controlled reload that preserves state
  if (expired) {
    return (
      <div
        role="alertdialog" aria-modal="true"
        aria-labelledby="session-expired-title" aria-describedby="session-expired-desc"
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{
          background: '#fff', borderRadius: '12px', padding: '32px',
          maxWidth: '400px', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}>
          <h2 id="session-expired-title" style={{ margin: '0 0 12px', fontSize: '20px', color: '#dc2626' }}>
            Session Expired
          </h2>
          <p id="session-expired-desc" style={{ color: '#666', fontSize: '14px', margin: '0 0 24px' }}>
            Your session has timed out, but your responses are saved. Click Reload to continue.
          </p>
          <button
            onClick={async () => {
              // Preserve email in sessionStorage before reload so OTP status can be re-fetched
              try {
                const formState = await secureSessionStorage.getItem('followup_form_state');
                if (formState) {
                  const data = typeof formState === 'string' ? JSON.parse(formState) : formState;
                  if (data.userEmail) {
                    secureSessionStorage.setItem('lockout_check_email', data.userEmail).catch(() => {});
                  }
                }
              } catch (e) { /* best effort */ }
              window.location.reload();
            }}
            style={{
              padding: '12px 32px', background: 'var(--color-primary, #088395)',
              color: '#fff', border: 'none', borderRadius: '8px',
              fontSize: '16px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {isVisible && (
        <div
          aria-label={`Session expires in ${formatTime(secondsLeft)}`}
          style={{
            fontSize: '12px',
            fontWeight: isWarning ? 600 : 400,
            color: isCritical ? '#dc2626' : isWarning ? '#d97706' : '#6b7280',
            textAlign: 'center',
            padding: '4px 0',
          }}
        >
          Session expires in: {formatTime(secondsLeft)}
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </>
  );
};

export default SessionTimer;
